import React from "react";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-dragdata";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import PropTypes from "prop-types";
import { DataTypes } from "../ChartComponents/DataTypes";

import {
  Chart as ChartJS,
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import WaterfallBarChart from "./WaterfallBarChart";

ChartJS.register(
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

class LineChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {
        labels: [],
        datasets: [],
      },
      options: this.buildOptions(this.props.chartOptions),
    };

    this.ref = null;
  }

  componentDidMount() {
    this.buildData();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps != this.props && !this.imgSrc) {
      this.ref.options = this.buildOptions(nextProps.chartOptions);
      this.buildData();
      this.ref.update();
    }

    return true;
  }

  buildOptions = (props) => {
    if (this.props.preview && this.props.businessPlan) {
      if (window.chartsForRender.indexOf(this.buildOptions) === -1) {
        window.chartsForRender.push(this.buildOptions);
      }
    }
    let options = {
      type: "line",
      barRoundness: 0.3,
      maintainAspectRatio: props.lockedAspect ? props.lockedAspect : false,
      elements: {
        point: {
          radius: 5,
        },
      },
      layout: {
        padding: {
          top: props.showTitle || props.showSubtitle ? 0 : 45,
          right: 60,
        },
      },
      animation: {
        duration: 1,
        onComplete: (animation) => {
          if (this.props.preview && this.props.businessPlan && this.ref) {
            this.imgSrc = this.ref.toBase64Image();
            this.forceUpdate(() => {
              window.amountOfChartsRendered++;
              global.Modeliks.NotifySubsctiptions("onChartRenderFinish");
            });
          }
        },
      },
      scales: {
        x: {
          grid: {
            display: props.xGrid ? props.xGrid : false,
            drawBorder: props.xGrid ? props.xGrid : true,
            borderWidth: 3,
          },
          ticks: {
            display: props.showHorizontalAxis,
            color: "#889299",
            font: {
              weight: 400,
              style: "normal",
              size: props.axisFontSize,
              lineHeight: "160%",
            },
          },
        },
        y: {
          grid: {
            display: props.yGrid ? props.yGrid : false,
            drawBorder: props.xGrid ? props.xGrid : false,
          },
          ticks: {
            display: props.showVerticalAxis,
            color: "#889299",
            maxTicksLimit: 7,
            minTicksLimit: 5,
            font: {
              weight: 400,
              style: "normal",
              size: props.axisFontSize,
              lineHeight: "160%",
            },
            callback: function (value) {
              if (value >= 1000 && value < 1000000) {
                return value / 1000 + "k";
              } else if (value >= 1000000) {
                return value / 1000000 + "mil";
              } else {
                return value;
              }
            },
          },
        },
      },
      plugins: {
        tooltip: {
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 14,
          },
        },
        title: {
          display: props.showTitle ? props.showTitle : false,
          text:
            props.title && props.titleAlign && props.titleAlign === "start"
              ? props.title
              : props.title
                ? props.title
                : "",
          align: props.titleAlign ? props.titleAlign : "center",
          color: "#000000",

          padding: {
            bottom: props.showSubtitle && props.showSubtitle === true ? 0 : 45,
          },
          font: {
            size: props.titleFontSize,
          },
        },
        subtitle: {
          display: props.showSubtitle ? props.showSubtitle : false,
          text:
            props.subtitle && props.titleAlign && props.titleAlign === "start"
              ? props.subtitle
              : props.subtitle
                ? props.subtitle
                : "",
          align: props.titleAlign ? props.titleAlign : "center",
          padding: {
            bottom: 45,
          },
          font: {
            size: props.subTitleFontSize,
          },
        },
        datalabels: {
          anchor: "end",
          align: "end",
          formatter: function (value, ctx) {
            if (props.showDataLabels) {
              return (
                (props.dataType === "currency" ? DataTypes[props.dataType] : "") +
                value.toLocaleString(undefined, {
                  minimumFractionDigits: props.datalabelsDecimalPoints,
                  maximumFractionDigits: props.datalabelsDecimalPoints,
                }) +
                (props.dataType === "percent" ? DataTypes[props.dataType] : "")
              );
            } else {
              return "";
            }
          },
          color: "#252525",
          font: {
            size: props.dataLabelsFontSize,
            weight: "700",
            lineHeight: "17px",
          },
        },
        legend: {
          display: props.displayLegend ? props.displayLegend : false,
          position: props.legendPosition ? props.legendPosition : "bottom",
          align: "center",
          labels: {
            fontSize: props.legendFontSize,
            usePointStyle: true,
          },
        },
      },
    };

    return options;
  };

  buildData = () => {
    let labels = [];
    let datasets = [];
    let rows = this.props.data;
    let colors = this.props.colors;

    rows[0].forEach((label, index) => {
      if (index !== 0) {
        labels.push(label);
      }
    });

    rows.forEach((row, index) => {
      if (index !== 0) {
        if (index >= colors.length) {
          colors = [...colors, ...colors];
          this.props.changeColors(colors);
        }
        let obj = {
          label: "",
          borderColor: colors[index - 1],
          backgroundColor: colors[index - 1],
          pointBackgroundColor: colors[index - 1],
          pointBorderColor: colors[index - 1],
          pointBorderWidth: 2,
          data: [],
        };
        row.forEach((data, indexData) => {
          if (indexData === 0) {
            obj.label = data;
          } else {
            let parsedData = parseFloat(data);
            if (parsedData % 1 === 0) {
              obj.data.push(parseInt(data));
            } else {
              obj.data.push(parsedData);
            }
          }
        });
        datasets.push(obj);
      }
    });

    this.state.data.labels = labels;
    this.state.data.datasets = datasets;
  };

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    if (this.imgSrc) {
      return <img src={this.imgSrc} style={{ height: "100%", width: "100%" }} />;
    }
    return (
      <Line
        type={"line"}
        data={this.state.data}
        options={this.state.options}
        ref={this.setRef}
        style={{
          background: this.props.chartOptions.backgroundColor,
          border: `2px solid ${this.props.chartOptions.borderColor}`,
        }}
      />
    );
  }
}

LineChart.config = {
  data: [
    ["", "label1", "label2", "label3"],
    ["ser1", "1", "4", "7"],
    ["ser2", "1", "2", "5"],
  ],
  colors: ["red", "green", "blue", "yellow"],
  dataGridConfig: {
    title: "blabla",
    subtitle: "blabla",
    canAddRows: true,
    canAddColumns: true,
    hasColumnHeaders: true,
    hasRowHeaders: true,
    rowSeries: false,
  },
  dataLalbelsConfig: {
    hideSeries: false,
    dataLabelsOptions: false,
  },
  legendAndGridlinesConfig: {
    hasGridlines: true,
  },
  gapWidthConfig: {
    haveGapWidth: false,
  },
};

LineChart.propTypes = {
  data: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
};

export default LineChart;
